import moment from 'moment';
import { ICalendarEvent } from 'model/calendar/events';
import { IEvent } from 'model/v2';
import { calendarEventsPrefVar } from 'utils/cache/filters';

/**
 * Cancel is asynchronous: events only fall off the calendar once the downstream
 * cancel completes (a few seconds later, via the calendarEvents subscription).
 * Unlike Create (appears instantly) and Reschedule (jumps instantly), that delay
 * leaves the tile unchanged, so the click looks like it did nothing. To give
 * immediate feedback we optimistically flag the cancelled event(s) with
 * isCancelInProgress, which ItemTooltip reads directly to show the "In-progress
 * Cancel in Axon" hover status right away — until the events are removed. We use
 * a dedicated flag (not crStatus/athenaStatus) because the planner tile overrides
 * those two fields with its own useEventSyncStatus values, which would discard an
 * optimistic status. The flag survives the tile's merge (only crStatus/athenaStatus
 * are overridden) and produces no tile styling change, so it is a hover-status-only
 * cue, matching the spirit of create/reschedule's instant feedback.
 *
 * For a recurrent (this-and-following / series) cancel, every later instance of
 * each affected series currently on the planner is flagged too, since they will
 * all fall off. Shared by the single-event CancelModal and the bulk BulkCancelForm.
 */
export const markCancelInProgressOptimistically = (
  cancelledEvents: IEvent[],
  recurrent: boolean
) => {
  if (!cancelledEvents.length) return;

  const selectedIds = new Set(cancelledEvents.map(e => e.id));

  // Earliest selected start-time per series master, for this-and-following
  // matching (only later instances of an affected series get flagged).
  const seriesEarliestStart = new Map<number, number>();
  if (recurrent) {
    cancelledEvents.forEach(e => {
      const masterId = e.master?.id;
      if (masterId == null) return;
      const start = moment(e.startDate).valueOf();
      const prev = seriesEarliestStart.get(masterId);
      if (prev === undefined || start < prev) {
        seriesEarliestStart.set(masterId, start);
      }
    });
  }

  const isTarget = (e: ICalendarEvent) => {
    if (e.id != null && selectedIds.has(e.id)) return true;
    const masterId = e.master?.id;
    if (recurrent && masterId != null && seriesEarliestStart.has(masterId)) {
      return (
        moment(e.startDate).valueOf() >=
        (seriesEarliestStart.get(masterId) as number)
      );
    }
    return false;
  };

  const updated = calendarEventsPrefVar().map(e =>
    isTarget(e) ? { ...e, isCancelInProgress: true } : e
  );
  calendarEventsPrefVar(updated);
};
